import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useMatrixSurveyTooltips } from "../hooks/useMatrixSurveyTooltips";
import { CircularProgress, Box, Button, Typography, Paper, Divider, Stack, TextField } from "@mui/material";
import useFetchQuestionnaireById from "../hooks/useFetchQuestionnaireById";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import CustomSurveyNav from "../components/CustomSurveyNav";
import useUpdateQuestionnaireMetadata from "../hooks/useUpdateQuestionnaireMetadata";
import BuildJsonModal from "../components/Modal/BuildJsonModal";

const QuestionnairePreviewPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useFetchQuestionnaireById(id);
  const isEditingInitial = location.state?.isEditing || false;
  const updateMetadata = useUpdateQuestionnaireMetadata();

  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({});
  const [hasChanged, setHasChanged] = useState(false);
  const [isEditing, setIsEditing] = useState(isEditingInitial);
  const [showJsonModal, setShowJsonModal] = useState(false);

  useEffect(() => {
    if (error && !loading) {
      setErrorMessage(error.message || "Error al cargar el cuestionario.");
      setShowError(true);
    }
  }, [error, loading]);
  useEffect(() => {
    if (data && !loading) {
      setFormData({
        title: data?.questionnaire?.title || "",
        external_id: data?.questionnaire?.external_id || "",
        scope: data?.questionnaire?.scope || "",
        instructions: data?.questionnaire?.instructions || "",
      });
    }
  }, [data, loading]);

  const questionnaireInfo = data?.questionnaire || data;
  const surveyJson = data?.survey?.[0]?.content;

  const surveyModel = useMemo(() => {
    if (!surveyJson) return null;

    const model = new Model(surveyJson);

    model.showNavigationButtons = false;
    model.showCompletedPage = false;
    model.showProgressBar = "off";
    model.width = "100%";
    model.checkErrorsMode = "onComplete";
    model.showQuestionNumbers = "off";

    return model;
  }, [surveyJson]);

  useMatrixSurveyTooltips(surveyModel);

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-60">
        <CircularProgress />
      </Box>
    );
  }

  const onEdit = async () => {
    const result = await updateMetadata(id, formData);
    console.log(result);
    if (result) {
      setShowSuccess(true);
      await refetch();
      setIsEditing(false);
    } else {
      setErrorMessage(error.message || "No se pudo actualizar la metadata del cuestionario");
      setShowError(true);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanged(true);
  };

  const renderField = (label, field, value) => {
    if (isEditing) {
      return (
        <>
          <Typography>
            <strong>{label}:</strong>
          </Typography>

          <TextField
            hiddenLabel
            fullWidth
            value={formData[field] || ""}
            onChange={(e) => handleInputChange(field, e.target.value)}
            variant="filled"
          />
        </>
      );
    }

    return (
      <Typography>
        <strong>{label}</strong> {value}
      </Typography>
    );
  };

  return (
    <Box
      className="questionnaire-view"
      sx={{
        p: 4,
        width: "100%",
        maxWidth: { xs: "100%", md: "1300px", xl: "1600px" },
        mx: "auto",
      }}
    >
      <Typography variant="h5" gutterBottom fontWeight={600}>
        {isEditing ? "Editar" : "Cuestionario: "} {questionnaireInfo?.title}
      </Typography>

      {/* Información básica */}
      <Paper sx={{ p: 3, mb: 4 }} elevation={3}>
        <Stack spacing={1}>
          {
            <Typography>
              <strong>ID:</strong> {questionnaireInfo?.id}
            </Typography>
          }
          {renderField("ID externo", "external_id", questionnaireInfo?.external_id)}
          {renderField("Título", "title", questionnaireInfo?.title)}
          <Typography>
            <strong>Tipo:</strong> {questionnaireInfo?.questionnaire_type}
          </Typography>
          {renderField("Alcance", "scope", questionnaireInfo?.scope)}
          {renderField("Instrucciones", "instructions", questionnaireInfo?.instructions)}
        </Stack>
        {/* Botón guardar en modo edicion */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {isEditing && (
            <Box mt={3}>
              <Button variant="contained" onClick={() => onEdit()} sx={{ ml: "auto", display: "block" }} disabled={!hasChanged}>
                Guardar
              </Button>
            </Box>
          )}
          {isEditing && (
            <Box mt={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(questionnaireInfo);
                }}
                sx={{ ml: "auto", display: "block" }}
              >
                Cancelar
              </Button>
            </Box>
          )}
        </Box>
        {!isEditing && (
          <Box mt={3} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={() => setIsEditing(true)}>
              Editar
            </Button>
            {surveyJson && (
              <Button variant="outlined" onClick={() => setShowJsonModal(true)}>
                Mostrar JSON
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Survey asociada */}
      <Typography variant="h6" gutterBottom fontWeight={500}>
        Vista previa
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {surveyModel ? (
        <Box sx={{ width: "100%", height: "max-content", "& .sd-root-modern": { overflowY: "hidden" } }}>
          <Box sx={{ mb: 2 }}>
            <CustomSurveyNav model={surveyModel} />
          </Box>
          <Survey model={surveyModel} />
          <CustomSurveyNav model={surveyModel} />
        </Box>
      ) : (
        <Typography color="text.secondary">No hay una encuesta asociada a este cuestionario.</Typography>
      )}

      {/* Botón volver */}
      <Box mt={4}>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </Box>

      {/* Modal para mostrar JSON guardado */}
      <BuildJsonModal open={showJsonModal} onClose={() => setShowJsonModal(false)} jsonData={surveyJson} loading={false} showOnly={true} />

      <SuccessSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Operación exitosa"
        message="Se ha actualizado la información"
      />

      {/* Snackbar de error */}
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Ha ocurrido un error" message={errorMessage} />
    </Box>
  );
};

export default QuestionnairePreviewPage;
